// tvVideos.js — las sesiones de TV, desde el servidor de la radio.
//
// Contrato: `TV_WEB_INSTRUCCIONES.md` (servidor, 26.08.26).
// Endpoint: https://listen.astra.fm/tv.json · CORS abierto · `max-age=0, must-revalidate` (304 por ETag; medido 05.09.26).
//
// **Sustituye al manager como fuente.** Hasta hoy los vídeos salían de
// `tv-videos-api.php`, contra su MySQL; ahora la fuente única es este JSON, que
// es lo que el equipo crea y edita en el dashboard `/studio/tv.html`. Seguir
// leyendo del manager dejaría fuera los vídeos nuevos y las ediciones.
//
// ⚠️ Cuatro cosas del contrato:
//
//   1. **`videos` es un array**, no un objeto indexado — al revés que
//      `actualidad.json`. Y los nombres de campo son los mismos que daba el
//      manager, a propósito, para que el cliente cambie poco.
//   2. **`canal` es siempre `riot`.** Flux se extinguió y sus vídeos no se
//      migraron, así que ya no hay que filtrar por canal.
//   3. **`published_at` puede ser `null`** —lo es en los treinta y nueve
//      migrados—, así que se ordena por `published_at || created_at`.
//   4. **`duration` viene vacío o nulo** en todos. No se pinta lo que no hay.
//
// Los vídeos creados en el dashboard llevan `origin: "studio"` y **no tienen**
// `managerId` ni `migratedAt`: no asumir que existen.
import cache from "@/services/globalCache";
import { artistSlug } from "@/services/editorialArtists";
import { LISTEN } from "./host";

const URL_TV = `${LISTEN}/tv.json`;
const TTL = 5 * 60 * 1000;

// La clave del caché compartido, en un sitio: la usan la lectura y el olvido.
const CLAVE = "tvJson";

/**
 * Olvida los vídeos guardados.
 *
 * 🔴 Este caché es el único de la web que **persiste en localStorage**, así que
 * sobrevive a recargar la página: sin invalidarlo, ni un F5 traía los vídeos
 * nuevos hasta que caducaran los cinco minutos.
 */
export function invalidar() {
  cache.invalidate(cache.key(CLAVE));
}

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** «2026-02-24 19:20:06» → «24 de febrero de 2026». */
function enLargo(fecha) {
  const [y, m, d] = (fecha || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} de ${MESES[m - 1]} de ${y}`;
}

/** El id de un enlace de YouTube, en cualquiera de sus formas. */
function idDeYouTube(url) {
  const m = String(url || "").match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{6,})/,
  );
  return m ? m[1] : "";
}

// Forma única para la web. Los campos del contrato no salen de aquí.
/**
 * Un texto del contrato, listo para pintarse.
 *
 * ⚠️ **Los espacios de sobra llegan de verdad.** Medido el 21.09.26: el
 * videoclip 59 viene con `name: "Nothing -  Nothing - A.C.D. …"`, con dos
 * espacios, porque su `song_title` empieza por espacio. Se ve en el título de
 * la pestaña, en la tarjeta y al pie del visor.
 *
 * ⚠️ **Solo se toca el espacio en blanco.** Lo demás es de la redacción: ese
 * mismo nombre repite el artista dos veces, y eso NO se arregla aquí, se
 * arregla en el dato.
 */
function limpia(texto) {
  return String(texto ?? "").replace(/\s+/g, " ").trim();
}

function normaliza(v) {
  const id = idDeYouTube(v.url);
  // Para ordenar y para la fecha visible. El contrato lo dice: uno u otro.
  const fecha = v.published_at || v.created_at || "";

  return {
    id: v.id,

    // La URL legible de la sección: `/tv/<slug>`.
    //
    // 🔴 Se prefiere el `slug` del contrato, aunque hoy no venga: cuando el
    // servidor lo publique, la web lo usa sin tocar una línea. El de casa es
    // solo el respaldo, y tiene la debilidad de todo slug derivado del título:
    // si la redacción reescribe el nombre, la URL cambia con él.
    //
    // Se reutiliza `artistSlug` en vez de escribir otro: hace exactamente esto
    // —fuera puntuación y diacríticos, espacios a guiones— y ya está probado.
    slug: v.slug || artistSlug(v.name || ""),

    name: limpia(v.name),
    artist: limpia(v.artist_name),
    song: limpia(v.song_title),
    // El nombre del programa, tal cual aparece en `programas.json`. Es lo que
    // permite agrupar las sesiones por programa.
    playlist: v.playlist || "",
    description: v.description || "",
    url: v.url || "",
    source: v.source || "",

    // Se usa el campo del contrato —el dashboard puede haberlo personalizado—
    // y NO se construye a mano.
    //
    // 🔴 Pero hace falta respaldo, y el `onerror` que sugiere el contrato **no
    // basta**: los treinta y nueve apuntan a `maxresdefault.jpg`, que YouTube
    // solo genera si el vídeo se subió en HD, y cuando no existe responde
    // **404 con una imagen gris de 120×90 en el cuerpo**. El navegador la
    // pinta, `onerror` no se dispara, y queda un hero de 120×90 estirado sin
    // ningún error a la vista. Por eso `DuotoneArt` mira además el tamaño de
    // lo que ha cargado.
    thumbnail: v.thumbnail_url || (id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : ""),
    // `hqdefault` lo genera SIEMPRE. ⚠️ Mide 480×360 —4:3 con bandas negras
    // arriba y abajo—, así que el hueco que lo pinte tiene que ser 16:9: al
    // recortar se come justo las bandas.
    thumbnailFallback: id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "",

    // 🔴 **La duración vuelve el 21.09.26.** Se había retirado con la nota «el
    // contrato lo publica vacío o nulo en los treinta y nueve vídeos», y eso ya
    // no es cierto: medidas hoy, **44 de 44** traen segundos enteros, de 2:45 a
    // 6:30. Comprobado además contra YouTube —el servidor la saca de allí— y
    // Butaca la necesita en la tarjeta y en el visor.
    duration: v.duration ?? null,

    date: fecha.slice(0, 10),
    dateLong: enLargo(fecha),

    // Orden interno, para no re-parsear en cada comparación.
    _orden: fecha,
  };
}

/**
 * Todas las sesiones, de la más nueva a la más vieja.
 * Ante un fallo devuelve un array vacío: sin vídeos la página se queda sin
 * archivo, no rota.
 */
export async function getVideos() {
  try {
    const data = await cache.getWith(cache.key(CLAVE), TTL, async () => {
      const r = await fetch(URL_TV);
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    });

    return (data?.videos || [])
      .map(normaliza)
      .sort((a, b) => b._orden.localeCompare(a._orden));
  } catch (error) {
    return [];
  }
}

/**
 * La sesión más reciente, o `null`. La usa la puerta de TV de la portada.
 * Comparte el caché con la lista: las dos salen del mismo JSON.
 */
export async function getLatestVideo() {
  const lista = await getVideos();
  return lista[0] || null;
}
